// CallingWindowEditor — reusable "only dial between these hours" control.
// Stored on a campaign as data.calling_window:
//   { enabled, source: "profile"|"custom", profile_id, start, end, tz, days[] }
// Shared by Voice Broadcast and the predictive/power/preview campaign editor.
import { Clock } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import RelationalPicker from "@/admin/components/RelationalPicker";

const TZS = [
  "Asia/Kolkata", "Asia/Singapore", "Asia/Dubai", "Europe/London",
  "America/New_York", "America/Los_Angeles", "Australia/Sydney", "UTC",
];
const DAYS = [
  { k: "mon", label: "Mon" }, { k: "tue", label: "Tue" }, { k: "wed", label: "Wed" },
  { k: "thu", label: "Thu" }, { k: "fri", label: "Fri" }, { k: "sat", label: "Sat" },
  { k: "sun", label: "Sun" },
];

export const EMPTY_CALLING_WINDOW = {
  enabled: false, source: "custom", profile_id: "",
  start: "09:00", end: "18:00", tz: "Asia/Kolkata",
  days: ["mon", "tue", "wed", "thu", "fri"],
};

export default function CallingWindowEditor({ value, onChange, testPrefix = "cw" }) {
  const cw = { ...EMPTY_CALLING_WINDOW, ...(value || {}) };
  const set = (patch) => onChange({ ...cw, ...patch });
  const toggleDay = (k) => {
    const days = cw.days?.includes(k) ? cw.days.filter((d) => d !== k) : [...(cw.days || []), k];
    set({ days });
  };

  return (
    <div className="space-y-3" data-testid={`${testPrefix}-block`}>
      <div className="flex items-center gap-2">
        <Switch data-testid={`${testPrefix}-enabled`} checked={!!cw.enabled}
          onCheckedChange={(v) => set({ enabled: !!v })} />
        <Label className="flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" /> Restrict to a calling window (working hours)
        </Label>
      </div>

      {cw.enabled && (
        <div className="pl-8 space-y-3">
          <p className="text-[11px] text-muted-foreground">
            Calls only go out inside this window. Outside it, dialing auto-pauses and
            resumes when the window re-opens — no calls after hours.
          </p>

          <div>
            <Label className="text-xs">Window source</Label>
            <Select value={cw.source || "custom"} onValueChange={(v) => set({ source: v })}>
              <SelectTrigger data-testid={`${testPrefix}-source`} className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">Custom times (set below)</SelectItem>
                <SelectItem value="profile">Working-Hours Profile</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {cw.source === "profile" ? (
            <div>
              <Label className="text-xs">Working-Hours Profile</Label>
              <RelationalPicker kind="working_hours_profiles" value={cw.profile_id}
                onChange={(v) => set({ profile_id: v })} testId={`${testPrefix}-profile`}
                placeholder="Pick a profile…" />
              <p className="text-[11px] text-muted-foreground mt-1">
                Manage profiles under Admin → Working Hours. Uses the profile's start/end,
                timezone and open days.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div><Label className="text-xs">Start time</Label>
                  <Input data-testid={`${testPrefix}-start`} type="time" value={cw.start}
                    onChange={(e) => set({ start: e.target.value })} className="h-9" /></div>
                <div><Label className="text-xs">End time</Label>
                  <Input data-testid={`${testPrefix}-end`} type="time" value={cw.end}
                    onChange={(e) => set({ end: e.target.value })} className="h-9" /></div>
                <div><Label className="text-xs">Timezone</Label>
                  <Select value={cw.tz || "Asia/Kolkata"} onValueChange={(v) => set({ tz: v })}>
                    <SelectTrigger data-testid={`${testPrefix}-tz`} className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{TZS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div>
                <Label className="text-xs">Days open</Label>
                <div className="flex flex-wrap gap-1.5 mt-1" data-testid={`${testPrefix}-days`}>
                  {DAYS.map((d) => {
                    const on = cw.days?.includes(d.k);
                    return (
                      <button type="button" key={d.k} data-testid={`${testPrefix}-day-${d.k}`}
                        onClick={() => toggleDay(d.k)}
                        className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                          on ? "bg-primary text-primary-foreground border-primary"
                             : "bg-transparent border-border hover:bg-muted/60"}`}>
                        {d.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Leave all off to allow every day. Overnight windows (e.g. 21:00 → 06:00) are supported.
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Format the backend's open-day indices (0=Mon..6=Sun) into a short label,
// compressing consecutive runs (e.g. [0,1,2,3,4] → "Mon–Fri").
const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
function formatDays(days) {
  if (!Array.isArray(days) || days.length === 0 || days.length >= 7) return null; // every day
  const sorted = [...new Set(days)].sort((a, b) => a - b);
  const parts = [];
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j += 1;
    parts.push(j > i ? `${DAY_SHORT[sorted[i]]}–${DAY_SHORT[sorted[j]]}` : DAY_SHORT[sorted[i]]);
    i = j + 1;
  }
  return parts.join(", ");
}

// Small inline status pill for the live console cards.
export function CallingWindowPill({ window: w, testId }) {
  if (!w || !w.enabled) return null;
  const open = !!w.open;
  const win = w.window || {};
  const timeLabel = win.start && win.end ? `${win.start}–${win.end} ${win.tz || ""}`.trim() : "window";
  const daysLabel = formatDays(win.days);
  const closedToday = !open && w.reason === "closed_day";
  // Build a message that makes the CLOSED reason unambiguous — a day-based
  // closure (e.g. today is Sat but the window only runs Mon–Fri) previously
  // looked like a bug because only the time range was shown.
  let text;
  if (open) {
    text = `Window open · ${timeLabel}${daysLabel ? ` · ${daysLabel}` : ""}`;
  } else if (closedToday) {
    text = `Closed today${w.local_day ? ` (${w.local_day.charAt(0).toUpperCase() + w.local_day.slice(1)})` : ""} · runs ${daysLabel || "every day"} ${timeLabel}`;
  } else {
    text = `Window closed · ${timeLabel}${daysLabel ? ` · ${daysLabel}` : ""}`;
  }
  return (
    <span data-testid={testId} title={text}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
        open ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
             : "bg-slate-200 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300"}`}>
      <Clock className="w-3 h-3" />
      {text}
    </span>
  );
}
